import { LitElement, html, css } from '@hh/global.js'

class HHBundleSelectionDrawer extends LitElement {
  static properties = {
    _trapFocus: {
      state: true,
    },
  }

  constructor() {
    super()

    this.overlay = document.querySelector('.js-bundle-selection-overlay')
    this.triggers = this.querySelectorAll('.js-bundle-selection-drawer-line')
    this.bundleConfig = document.querySelector('hh-bundle-items-picker')

    this.handleSelectClick = this.handleSelectClick.bind(this)
    this.handleBodyClick = this.handleBodyClick.bind(this)
    this._calculateBundlePrice = this._calculateBundlePrice.bind(this)
    this._calculateCompareLinePrice = this._calculateCompareLinePrice.bind(this)

    // activate focus trap only after the transition ends to always trap focus on open drawers
    this.addEventListener('transitionend', (e) => {
      if (e.target !== this) return
      this._trapFocus = this.classList.contains('active')
    })
  }

  connectedCallback() {
    super.connectedCallback()
    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', this.handleSelectClick)
    }, false)
    document.body.addEventListener('click', this.handleBodyClick)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.triggers.forEach(trigger => {
      trigger.removeEventListener('click', this.handleSelectClick)
    })
    document.body.removeEventListener('click', this.handleBodyClick)
  }

  handleSelectClick(e) {
    e.preventDefault()
    const { target } = e

    if (target) {
      const item = target?.closest('.bundle__selection-drawer__line')
      const variant = JSON.parse(item.querySelector('[data-item]').textContent)
      const image_elem = item?.querySelector('.bundle__selection-drawer__line-image').innerHTML

      this.triggers.forEach(trigger => trigger.classList.remove('active'))
      item.classList.add('active')

      const imageTarget = document.querySelector(`.js-bundle-item-line-image-${variant.index}`)
      if (imageTarget) imageTarget.innerHTML = image_elem
      document.querySelector(`.js-bundle-item-line-title-${variant.index}`).innerHTML = variant.title
      const idInput = document.querySelector(`#bundle_item_id_${variant.index}`)
      const priceInput = document.querySelector(`#bundle_item_price_${variant.index}`)
      idInput.value = variant.id
      idInput.removeAttribute('data-unselected')
      priceInput.value = variant.price
      priceInput.removeAttribute('data-unselected')
      if (variant.available) {
        priceInput.removeAttribute('disabled')
      } else {
        priceInput.setAttribute('disabled', true)
      }
      document.querySelector(`#bundle_item_available_${variant.index}`).value = variant.available
      document.querySelector(`#bundle_item_properties_${variant.index}`).value = !!variant.line_properties ? JSON.stringify(variant.line_properties) : ''
      const line = document.querySelector(`#bundle_item_available_${variant.index} + .js-bundle-item-line`)
      line?.classList[variant.available ? 'remove' : 'add']('soldout')
      if (line?.classList.contains('is-unselected')) {
        line.classList.remove('is-unselected')
        line.querySelector('.js-bundle-empty-trigger')?.classList.add('is-hidden')
        imageTarget?.classList.remove('is-hidden')
        if (line.dataset.canEdit === 'true') {
          line.querySelector('.js-bundle-selected-edit')?.classList.remove('is-hidden')
        }
      }

      const learnMoreButton = document.querySelector(`.js-bundle-item-line-learn-more-${variant.index}`)
      if (learnMoreButton && variant.product_id) {
        learnMoreButton.dataset.id = variant.product_id
        learnMoreButton.classList.remove('is-hidden')
      }

      this._calculateBundlePrice()
      this._calculateCompareLinePrice(variant.price)
      this.closeFn()
    }
  }

  isDetailDrawerOpen() {
    return !!document.querySelector('.js-bundle-detail-drawer.active')
  }

  handleBodyClick(e) {
    // the detail drawer is layered on top — let it handle its own closing
    if (this.isDetailDrawerOpen()) return

    const path = e.composedPath()

    if (
      path.some((node) => {
        return node.matches && node.matches('hh-bundle-selection-drawer')
      })
    ) {
      return true
    }

    if (!e.defaultPrevented && this.isOpen()) {
      this.closeFn()
    }
  }

  _calculateBundlePrice() {
    const discount_fixed = Number(this.bundleConfig.dataset.discountFixed) || 0
    const discount_percent = Number(this.bundleConfig.dataset.discountPercent) || 0
    this.currency_symbol = this.bundleConfig.dataset.currencySymbol || '$'
    const atc_label = this.bundleConfig.dataset.label || ''

    const quantityInput = document.querySelector('.js-pdp-quantity-input')
    const quantity = quantityInput?.getValue() || 1


    let total_price = 0
    document.querySelectorAll('.js-bundle-item-price').forEach(item => total_price = total_price + Number(item.value))
    let last_price = total_price * quantity

    if (!document.querySelector('.js-bundle-item-price:disabled')) {
      if (discount_fixed > 0) {
        last_price = total_price - discount_fixed
      } else if (discount_percent > 0) {
        last_price = total_price * (1 - discount_percent / 100)
      }
    }

    const formatted_total_price = HH.formatMoney(total_price, window.shopSettings.money_with_currency_format)
    const formatted_last_price = HH.formatMoney(last_price, window.shopSettings.money_with_currency_format)
    const hasUnselected = !!this.bundleConfig?.querySelector('.js-bundle-item-id[data-unselected="true"]')
    const fromLabel = this.bundleConfig?.dataset.fromLabel || 'From'
    const priceLabel = hasUnselected ? `${fromLabel} ${formatted_last_price}` : formatted_last_price

    document.querySelector('.js-single-atc-price').innerHTML = priceLabel

    const stickyATCButton = document.querySelector('.js-sticky-atc')
    const atcButton = document.querySelector('.js-single-atc')
    const priceButtonContent = `${atc_label} - ${priceLabel}`
    atcButton.setAttribute('data-price', total_price)
    atcButton.setAttribute('text', priceButtonContent)

    if (stickyATCButton) {
      stickyATCButton.buttonContent = priceButtonContent
    }

    // PDP - price
    let price_wrapper = document.querySelectorAll('.single-product .product-heading__tagline-price')

    if (total_price > last_price) {
      price_wrapper.forEach(wrapper => {
        wrapper.querySelector('.price').classList.add('price--on-sale')
        wrapper.querySelector('.price__sale').querySelector('.price-item--regular').innerHTML = formatted_total_price
        wrapper.querySelector('.price__sale').querySelector('.price-item--sale').innerHTML = formatted_last_price
      })
    } else {
      price_wrapper.forEach(wrapper => {
        wrapper.querySelector('.price').classList.remove('price--on-sale')
        wrapper.querySelector('.price__regular').querySelector('.price-item--regular').innerHTML = formatted_total_price
      })
    }

    this.checkAvailability()
  }

  checkAvailability() {
    const hasSoldoutItem = Array.from(this.bundleConfig.querySelectorAll('.js-bundle-item-available')).some(input => input.value === 'false')
    const hasUnselected = !!this.bundleConfig.querySelector('.js-bundle-item-id[data-unselected="true"]')
    const atcButton = document.querySelector('.js-single-atc')
    const stickyATCButton = document.querySelector('.js-sticky-atc')

    if (hasSoldoutItem) {
      atcButton.setAttribute('text', this.dataset.soldoutLabel)
      atcButton.setAttribute('disabled', '')
      stickyATCButton?.setAttribute('disabled', '')
    } else if (hasUnselected) {
      atcButton.setAttribute('disabled', '')
      stickyATCButton?.setAttribute('disabled', '')
    } else {
      atcButton.removeAttribute('disabled')
      stickyATCButton?.removeAttribute('disabled')
    }
  }

  _calculateCompareLinePrice(selected_variant_price) {
    selected_variant_price = Number(selected_variant_price) || 0
    let variant_price,
      diff_price,
      formatted_diff_price,
      compare_price_container
    this.triggers.forEach(variant => {
      compare_price_container = variant.querySelector('.js-bundle-line-compare-price')
      variant_price = Number(variant?.dataset?.price) || 0
      diff_price = variant_price - selected_variant_price
      const hideDecrease = this.dataset.hideNegativePrice === 'true'
      if (diff_price > 0) {
        formatted_diff_price = HH.formatMoney(diff_price, window.shopSettings.money_with_currency_format)
        compare_price_container.innerHTML = `+(${formatted_diff_price})`
      } else if (diff_price < 0 && !hideDecrease) {
        formatted_diff_price = HH.formatMoney(Math.abs(diff_price), window.shopSettings.money_with_currency_format)
        compare_price_container.innerHTML = `-(${formatted_diff_price})`
      } else {
        compare_price_container.innerHTML = ''
      }
    })
  }

  isOpen() {
    return !!document.body.classList.contains('no-scroll')
  }

  closeFn() {
    const openDrawer = document.querySelector('.js-bundle-selection-drawer.active')

    document.querySelectorAll('.js-bundle-selection-drawer').forEach(item => item.classList.remove('active'))
    this.overlay.classList.remove('active')
    this.bundleConfig?.unlockScroll()
    document.querySelector('html').classList.remove('overflow-hidden')

    this.restoreFocus(openDrawer)
  }

  restoreFocus(drawer) {
    if (!drawer) return

    const triggers = Array.from(document.querySelectorAll(`[data-target="${drawer.id}"]`))
    const trigger = triggers.find((el) => !el.classList.contains('is-hidden')) || triggers[0]
    if (trigger && !trigger.hasAttribute('disabled')) {
      trigger.focus({ preventScroll: true })
    }
  }

  onToggleSelect(event) {
    event?.detail?.event?.preventDefault()
    const { target } = event
  }

  closeWithEscape(e) {
    if (this.isDetailDrawerOpen()) return

    if (e.code.toUpperCase() === 'ESCAPE') {
      this.closeFn()
    }
  }

  render() {
    return html`<div>
      <hh-focus-trap
        active="${this._trapFocus}"
        @keyup="${this.closeWithEscape}"
      >
        <slot name="close" @click="${this.closeFn}"></slot>
        <slot name="error"></slot>
        <slot
          name="body"
          @toggleSelect="${this.onToggleSelect}"
        ></slot>
      </hh-focus-trap>
    </div> `
  }

  static styles = [css``]
}

customElements.define('hh-bundle-selection-drawer', HHBundleSelectionDrawer)
